import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Round 53 · "User cancelled your job · GHS 5 inconvenience pay" modal.
 *
 * Mounted globally (in App.js) for service-provider users. Polls
 * `/api/sp/me/cancellation-alert` every 20 s; if the server has a pending
 * alert (because a user just hit their 4th cancellation strike), shows a
 * big full-screen modal. Single dismiss button.
 */
export default function CancellationAlertModal() {
  const { user } = useAuth();
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "service_provider") return;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get("/sp/me/cancellation-alert");
        if (!stop && r.data?.alert) setAlert(r.data.alert);
      } catch (_) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 20000);
    return () => { stop = true; clearInterval(t); };
  }, [user]);

  if (!alert) return null;

  return (
    <div
      data-testid="cancel-alert-modal"
      className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/85 backdrop-blur-sm p-6"
    >
      <div className="max-w-md w-full bg-gradient-to-br from-[#13131E] via-[#1E1340] to-[#13131E] border border-[#EF4444]/40 rounded-3xl p-7 sm:p-9 text-center space-y-4 shadow-[0_30px_80px_-20px_rgba(239,68,68,0.45)]">
        <div className="mx-auto w-16 h-16 rounded-full bg-[#EF4444]/15 border-2 border-[#EF4444]/50 flex items-center justify-center">
          <X size={32} className="text-[#EF4444]" />
        </div>
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#EF4444]">Request Cancelled</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight">
          Request by user <span className="text-[#A78BFA]">{alert.user_name}</span> has been Cancelled
        </h1>
        <div className="rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30 px-5 py-4 flex items-center gap-3 justify-center">
          <Sparkles size={18} className="text-[#10B981]" />
          <span className="text-sm text-white">
            <span className="font-mono font-black text-lg text-[#10B981]">GHS {Number(alert.amount_ghs).toFixed(2)}</span>{" "}
            will be added to your wallet as an inconvenience cost
          </span>
        </div>
        <div className="text-base sm:text-lg font-display font-bold text-[#A78BFA]">THANK YOU.</div>
        <button
          onClick={() => setAlert(null)}
          data-testid="cancel-alert-dismiss"
          className="w-full py-3 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-xl mt-2"
        >
          OK, got it
        </button>
      </div>
    </div>
  );
}
